import { DivConst } from 'constants/divConst';

import type { QuizEditFormStateType } from 'types/page/quizEditFormStateType';
import { isEmpty } from 'utils/app';

/**
 * HTML フィールドが実質空かどうかを判定する。
 * タグを除去したテキストが空の場合、または値が null / undefined / 空文字の場合に true を返す。
 */
export function isHtmlFieldEmpty(html: string | null | undefined): boolean {
  if (html === null || html === undefined || html === '') {
    return true;
  }
  // HTML タグを除去してテキスト部分だけ取り出す
  const text = html.replace(/<[^>]*>/g, '');
  return isEmpty(text);
}

/**
 * respondentFields の中に getFlag=true のフィールドが 1 件以上あるかを返す。
 */
export function hasAnyRespondentFieldGetFlag(values: QuizEditFormStateType): boolean {
  return values.respondentFields.some(f => f.getFlag);
}

/**
 * lineUrl が必須入力かどうかを返す。
 * lineFlag が enable 値の場合に true。
 */
export function isLineUrlRequired(values: QuizEditFormStateType): boolean {
  return Number(values.lineFlag) === DivConst.items.lineFlag.enable.value;
}

/**
 * 回答者情報の取得項目（respondentMailDiv / getFlag）の整合性を検証する。
 * 「取得項目なし」以外を選択しているのに取得項目チェックが 0 件の場合にエラーを返す。
 * エラーがない場合は null を返す。
 */
export function validateRespondentMailDivRequired(
  values: QuizEditFormStateType
): { name: string; message: string } | null {
  if (
    Number(values.respondentMailDiv) !== DivConst.items.quizRespondentMail.none.value &&
    !isEmpty(values.respondentMailDiv) &&
    !hasAnyRespondentFieldGetFlag(values)
  ) {
    return {
      name: 'respondentMailDiv',
      message: '回答者情報の取得項目を 1 件以上選択してください',
    };
  }
  return null;
}

/**
 * フォーム全体の必須バリデーションを実行し、エラー情報の配列を返す。
 * エラーがない場合は空配列を返す。
 */
export function collectRequiredErrors(
  values: QuizEditFormStateType
): Array<{ name: string; message: string }> {
  const errors: Array<{ name: string; message: string }> = [];

  // トップレベルの文字列フィールド
  const requiredStringFields: Array<{ name: keyof QuizEditFormStateType; label: string }> = [
    { name: 'privacyPolicyUrl', label: 'プライバシーポリシーURL' },
    { name: 'termsUrl', label: '利用規約URL' },
    { name: 'copyright', label: 'コピーライト' },
    { name: 'reportMailSenderAddress', label: '送信元メールアドレス' },
    { name: 'description', label: '説明文' },
  ];

  for (const field of requiredStringFields) {
    if (isEmpty(values[field.name])) {
      errors.push({ name: field.name, message: `${field.label}を入力してください` });
    }
  }

  // HTML フィールド
  if (isHtmlFieldEmpty(values.reportMailSignatureHtml)) {
    errors.push({ name: 'reportMailSignatureHtml', message: 'メール署名を入力してください' });
  }

  // クイズテンプレート
  if (isEmpty(values.quizTemplateId)) {
    errors.push({ name: 'quizTemplateId', message: 'テンプレートを選択してください' });
  }

  // 回答者メール＆フィールド
  const respondentMailDivError = validateRespondentMailDivRequired(values);
  if (respondentMailDivError) {
    errors.push(respondentMailDivError);
  }

  // LINE URL
  if (isLineUrlRequired(values) && isEmpty(values.lineUrl)) {
    errors.push({ name: 'lineUrl', message: 'LINE URL を入力してください' });
  }

  // 問題
  values.questions.forEach((q, qi) => {
    if (isEmpty(q.title)) {
      errors.push({ name: `questions.${qi}.title`, message: '問題タイトルを入力してください' });
    }
    if (isHtmlFieldEmpty(q.descriptionHtml)) {
      errors.push({
        name: `questions.${qi}.descriptionHtml`,
        message: '問題説明を入力してください',
      });
    }
    if (isEmpty(q.reportMailDescriptionHtml)) {
      errors.push({
        name: `questions.${qi}.reportMailDescriptionHtml`,
        message: '問題メール説明を入力してください',
      });
    }
    q.options.forEach((opt, oi) => {
      if (isEmpty(opt.title)) {
        errors.push({
          name: `questions.${qi}.options.${oi}.title`,
          message: '選択肢タイトルを入力してください',
        });
      }
    });
  });

  // 結果
  values.results.forEach((r, ri) => {
    if (isEmpty(r.title)) {
      errors.push({ name: `results.${ri}.title`, message: '結果タイトルを入力してください' });
    }
    if (isHtmlFieldEmpty(r.descriptionHtml)) {
      errors.push({
        name: `results.${ri}.descriptionHtml`,
        message: '結果説明を入力してください',
      });
    }
  });

  return errors;
}
